import { Link, useNavigate } from "@tanstack/react-router";
import { useI18n } from "../i18n/I18nProvider";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Menu, type MenuItem } from "../components/ui/Menu";
import { DenseTable, QuotaBar, StatusLabel, TD_CLASS, TH_CLASS, type MarkTone } from "./dense";
import { formatBytes } from "./format";
import { workspaceStatus } from "./workspace-status";
import type { AdminWorkspace, WorkspaceStatus } from "./types";

export interface TenantTableProps {
  workspaces: readonly AdminWorkspace[];
  busy: boolean;
  onEdit: (workspace: AdminWorkspace) => void;
  onTransfer: (workspace: AdminWorkspace) => void;
  onDelete: (workspace: AdminWorkspace) => void;
  onRestore: (workspace: AdminWorkspace) => void;
  onSuspend: (workspace: AdminWorkspace) => void;
  onUnsuspend: (workspace: AdminWorkspace) => void;
  onActAs: (workspace: AdminWorkspace) => void;
}

const MARK: Record<WorkspaceStatus, MarkTone> = {
  active: "blue",
  suspended: "red",
  pending: "hollow",
  deleted: "muted",
};

/** Workspace label grid: name, owner, players, storage, status, Act as, and the overflow menu. */
export function TenantTable(props: TenantTableProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { workspaces, busy } = props;

  if (workspaces.length === 0) {
    return <EmptyState>{t("admin.empty")}</EmptyState>;
  }

  const menuFor = (workspace: AdminWorkspace): MenuItem[] => {
    const status = workspaceStatus(workspace);
    const items: (MenuItem | null)[] = [
      {
        label: t("admin.action.openDetails"),
        onSelect: () =>
          void navigate({
            to: "/admin/workspaces/$workspaceId",
            params: { workspaceId: workspace.id },
          }),
      },
      { label: t("admin.action.edit"), disabled: busy, onSelect: () => props.onEdit(workspace) },
      {
        label: t("admin.action.transfer"),
        disabled: busy,
        onSelect: () => props.onTransfer(workspace),
      },
      status === "active"
        ? {
            label: t("admin.action.suspend"),
            disabled: busy,
            onSelect: () => props.onSuspend(workspace),
          }
        : null,
      status === "suspended"
        ? {
            label: t("admin.action.unsuspend"),
            disabled: busy,
            onSelect: () => props.onUnsuspend(workspace),
          }
        : null,
      status === "deleted"
        ? { label: t("common.restore"), disabled: busy, onSelect: () => props.onRestore(workspace) }
        : {
            label: t("common.delete"),
            tone: "danger",
            disabled: busy,
            onSelect: () => props.onDelete(workspace),
          },
    ];
    return items.filter((item): item is MenuItem => item !== null);
  };

  return (
    <DenseTable label={t("admin.workspaces.title")}>
      <thead>
        <tr>
          <th scope="col" className={TH_CLASS}>
            {t("admin.col.name")}
          </th>
          <th scope="col" className={`${TH_CLASS} hidden md:table-cell`}>
            {t("admin.col.owner")}
          </th>
          <th scope="col" className={`${TH_CLASS} hidden md:table-cell`}>
            {t("admin.col.players")}
          </th>
          <th scope="col" className={`${TH_CLASS} hidden md:table-cell`}>
            {t("admin.col.storage")}
          </th>
          <th scope="col" className={TH_CLASS}>
            {t("admin.col.status")}
          </th>
          <th scope="col" className={`${TH_CLASS} text-right`}>
            <span className="sr-only">{t("admin.col.actions")}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {workspaces.map((workspace) => {
          const status = workspaceStatus(workspace);
          return (
            <tr
              key={workspace.id}
              className="transition-colors duration-[120ms] hover:bg-blue-wash/40"
            >
              <td className={TD_CLASS}>
                <Link
                  to="/admin/workspaces/$workspaceId"
                  params={{ workspaceId: workspace.id }}
                  className="inline-flex min-h-11 items-center font-display text-lg leading-tight font-bold tracking-[0.01em] text-ink uppercase hover:text-blue"
                >
                  {workspace.name}
                </Link>
                <p className="text-micro text-ink-3">{workspace.plan}</p>
              </td>
              <td className={`${TD_CLASS} hidden text-ink-2 md:table-cell`}>
                {workspace.ownerEmail}
                {status === "pending" && workspace.pendingInviteEmail ? (
                  <p className="text-micro text-ink-3">
                    {t("admin.pendingInvite", { email: workspace.pendingInviteEmail })}
                  </p>
                ) : null}
              </td>
              <td className={`${TD_CLASS} hidden tabular-nums md:table-cell`}>
                {workspace.persons} / {workspace.quotaPersons}
              </td>
              <td className={`${TD_CLASS} hidden md:table-cell`}>
                <QuotaBar
                  fraction={
                    workspace.quotaBytes > 0 ? workspace.bytesUsed / workspace.quotaBytes : 0
                  }
                  label={t("admin.quota", {
                    used: formatBytes(workspace.bytesUsed),
                    quota: formatBytes(workspace.quotaBytes),
                  })}
                />
              </td>
              <td className={TD_CLASS}>
                <StatusLabel tone={MARK[status]}>{t(`admin.status.${status}`)}</StatusLabel>
              </td>
              <td className={`${TD_CLASS} text-right`}>
                <div className="inline-flex items-center justify-end gap-1">
                  <Button
                    size="sm"
                    className="whitespace-nowrap"
                    disabled={busy || status === "pending"}
                    onClick={() => props.onActAs(workspace)}
                  >
                    {t("admin.action.actAs")}
                  </Button>
                  <Menu
                    label={t("roster.moreActions", { name: workspace.name })}
                    items={menuFor(workspace)}
                  />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </DenseTable>
  );
}
